import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { ApiMiddlewarePayload, ApiResponse } from '@ocean/api'
import { SessionUtils, LoggerUtils } from '@ocean/utils'
import { LoginType } from 'types'
import { AppThunk } from 'store'
import { createApiAction } from 'api'
import { UserUtils } from 'utils'
import { userLoggedOut, logoutUser } from 'core/session/slices/session'
import { redirectToSta } from 'core/staIntegration/slices/staIntegration'
import { getAccount } from 'modules/Auth/api/authService'
import { GetAccountResponse, AccountTypes } from 'modules/Auth/types'
import { setHighlightedViewedState } from 'modules/Notifications/slices/notifications'
import { HighlightedNotificationsUtils } from 'modules/Notifications/utils'
import { userStaAuthentication } from 'modules/redirects/StaRedirect/api/staRedirectService'
import { StaAuthenticationQueryParams } from 'modules/redirects/StaRedirect/types'
import { StaRedirectState, ApiStaAuthenticationResponse } from './types'

const initialState: StaRedirectState = {
  authenticationFromSta: {},
}

const staRedirectSlice = createSlice({
  name: 'staRedirect',
  initialState,
  reducers: {
    userAuthenticated(
      state,
      action: PayloadAction<ApiMiddlewarePayload<ApiStaAuthenticationResponse>>
    ) {
      state.authenticationFromSta = action.payload
    },
  },
  extraReducers: builder => {
    builder.addCase(userLoggedOut, () => {
      return initialState
    })
  },
})

const { userAuthenticated } = staRedirectSlice.actions

export default staRedirectSlice.reducer

const handleStaUserAuthentification =
  (response: ApiMiddlewarePayload<ApiStaAuthenticationResponse>): AppThunk =>
  async dispatch => {
    const { error, result } = response
    if (!error && result?.staAuthentication) {
      const { staAuthentication } = result
      SessionUtils.storeSession(staAuthentication.sessionToken)

      const account: ApiResponse<GetAccountResponse> = await getAccount()
      const { success, content } = account

      if (success && content) {
        // Check special user and redirect to STA
        if (content.type === AccountTypes.SpecialUser) {
          LoggerUtils.info('Logged in as SpecialUser')
          // TODO: Discuss changed flow for special users
          if (false) {
            dispatch(redirectToSta())
            return
          }
        }

        // Prevent show Highlighted Notifications popup in the current session
        HighlightedNotificationsUtils.markHighlightedNotificationsViewed()
        dispatch(setHighlightedViewedState(true))

        UserUtils.storeUserAccountInfo(content)
        dispatch(userAuthenticated(response))
        return
      } else {
        dispatch(logoutUser())
        dispatch(userAuthenticated({ error: account.error }))
        return
      }
    }
    dispatch(userAuthenticated(response))
  }

export const authenticateUserFromSta = (authParams: StaAuthenticationQueryParams) => {
  const { ost, company, userId } = authParams
  UserUtils.storeUserLoginParams({
    customer: company,
    userId,
    password: ost,
    loginType: LoginType.Sta,
  })

  return createApiAction({
    apiCalls: {
      staAuthentication: () => userStaAuthentication(authParams),
    },
    payloadAction: userAuthenticated,
    nextAction: handleStaUserAuthentification,
  })
}
